import React, { useMemo, useState, useEffect } from 'react';

import ArtistModal from './ArtistModal';
import ImageModal from './ImageModal';
import ARTIST_ID_MAP from './artists.js';

import './Plaque.css';

function Plaque({ piece, artistBios, onVideoPlay, role = 'artist' }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentItem, setCurrentItem] = useState(null);
  const [plaqueLoaded, setPlaqueLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeArtist, setActiveArtist] = useState(null);
  const [enlargedImage, setEnglargedImage] = useState(null);

  const normalize = (s) => s?.toLowerCase().trim();

  // --- Build artist names array with resolved IDs ---
  const artistNames = [];

  ['Artist 1', 'Artist 2'].forEach((field) => {
    if (piece[field]) {
      const rawName = piece[field];
      const key = normalize(rawName);
      if (!ARTIST_ID_MAP[key]) {
        console.warn(`Artist not found in ARTIST_ID_MAP: "${rawName}" normalized as "${key}"`);
      }
      artistNames.push({
        name: rawName,
        img: `/beads/${normalize(rawName)}_result.webp`,
        id: ARTIST_ID_MAP[key],
      });
    }
  });

  // --- helper to resolve media ---
  const resolveMediaSource = (sources) => {
    if (!sources?.length) return {};
    const imageSrc = sources.find(s => s.type === 'image')?.src;
    const videoSrc = sources.find(s => s.type === 'video')?.src;
    return { image: imageSrc, video: videoSrc };
  };

  // --- generate carousel items including video + embeds ---
  const carouselItems = useMemo(() => {
    const count_photos = parseInt(piece["Num Photos"]);
    const count_videos = parseInt(piece["Num Videos"])
    const baseName = piece["Piece ID"];
    const items = [];
    let index = 0;

    if (count_videos && baseName) {
      for (let i = index; i < count_videos; i++) {
        index = i + 1;
        items.push({
          type: 'media',
          sources: [
            { type: 'video', src: `/pieces/${baseName}_${index}.webm` },
          ],
        });
      }
    }

    if (count_photos && baseName) {
      for (let i = index; i < count_photos + count_videos; i++) {
        const index = i + 1;
        items.push({
          type: 'media',
          sources: [
            { type: 'image', src: `/pieces/${baseName}_${index}.webp` },
          ],
        });
      }
    }

    if (piece["Embed Link"]) {
      const link = piece["Embed Link"];

      const vimeoIDMatch = link.match(/vimeo\.com\/(\d+)/);
      if (vimeoIDMatch) {
        items.push({
          type: 'vimeo',
          src: `https://player.vimeo.com/video/${vimeoIDMatch[1]}`,
        });
      }

      if (link.includes('instagram.com')) {
        items.push({
          type: 'instagram',
          src: link.split('?')[0],
        });
      }
    }

    return items;
  }, [piece]);

  console.log(piece["Piece ID"], carouselItems)

  // --- preload first item ---
  useEffect(() => {
    if (!carouselItems.length) return;

    let canceled = false;
    setCurrentIndex(0);
    setCurrentItem(null);
    setPlaqueLoaded(false);

    const first = carouselItems[0];

    if (first.type === 'media') {
      const { image, video } = resolveMediaSource(first.sources);
      const img = new Image();
      img.src = image;

      img.onload = () => {
        if (!canceled) {
          setCurrentItem({ type: 'image', src: image });
          setPlaqueLoaded(true);
        }
      };

      img.onerror = () => {
        if (!canceled) {
          setCurrentItem({ type: 'video', src: video });
          setPlaqueLoaded(true);
        }
      };
    } else {
      setCurrentItem({ type: first.type, src: first.src });
      setPlaqueLoaded(true);
    }

    return () => {
      canceled = true;
    };
  }, [piece, carouselItems]);

  useEffect(() => {
    const item = carouselItems[currentIndex];
    if (item?.type === 'instagram' && window.instgrm) {
      window.instgrm.Embeds.process();
    }
  }, [currentIndex, carouselItems]);

  const showItemAt = (index) => {
    setPlaqueLoaded(false);
    const item = carouselItems[index];

    if (item.type === 'media') {
      const { image, video } = resolveMediaSource(item.sources);
      const img = new Image();
      img.src = image;

      img.onload = () => {
        setCurrentIndex(index);
        setCurrentItem({ type: 'image', src: image });
        setPlaqueLoaded(true);
      };

      img.onerror = () => {
        setCurrentIndex(index);
        setCurrentItem({ type: 'video', src: video });
        setPlaqueLoaded(true);
      };
    } else {
      setCurrentIndex(index);
      setCurrentItem({ type: item.type, src: item.src });
      setPlaqueLoaded(true);
    }
  };

  const prevItem = () =>
    showItemAt((currentIndex - 1 + carouselItems.length) % carouselItems.length);

  const nextItem = () =>
    showItemAt((currentIndex + 1) % carouselItems.length);

  function renderDetails(details) {
    if (!details) return null;

    const trimmed = details.trim();

    // If it doesn't look like a structured list, render raw
    if (!trimmed.includes('Song:')) {
      return <p className="pieceDetails">{details}</p>;
    }

    // Strip wrapping quotes and brackets (handles """, ", [, ])
    const cleaned = trimmed
      .replace(/^"+|"+$/g, '')   // remove outer quotes (including """)
      .replace(/^\[|\]$/g, '');  // remove surrounding brackets

    // Split into song entries
    const songs = cleaned
      .split(/Song:/)
      .map(s => s.trim())
      .filter(Boolean);

    return (
      <div className="pieceDetails">
        {songs.map((song, i) => (
          <div key={i}><i>{song}</i></div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className='plaque'>
        <div className='imgCarousel'>
          {carouselItems.length > 0 && (
            <div className='carouselWrapper'>
              {!plaqueLoaded && (
                <div className="spinner-wrapper">
                  <div className="spinner" />
                </div>
              )}

              {currentItem?.type === 'image' && (
                <div
                  className="carouselMediaWrapper"
                  onClick={() => setEnglargedImage(currentIndex)}
                >
                  <img
                    src={currentItem.src}
                    alt={`Slide ${currentIndex + 1}`}
                    className="carouselImage"
                    loading="eager"
                  />
                </div>
              )}

              {currentItem?.type === 'video' && enlargedImage === null && (
                <div
                  className="carouselMediaWrapper"
                  onClick={(e) => {
                    if (window.innerWidth > 768) return; // desktop: let native controls handle it

                    const video = e.currentTarget.querySelector('video');
                    if (!video) return;

                    // Mobile: toggle play/pause
                    if (video.paused) {
                      // Pause all other videos first
                      if (onVideoPlay) onVideoPlay(video);

                      // Must call play AFTER direct user click
                      const playPromise = video.play();
                      if (playPromise !== undefined) {
                        playPromise.catch((err) => {
                          console.warn("Video playback failed:", err);
                        });
                      }
                    } else {
                      video.pause();
                    }
                  }}
                >
                  <video
                    src={currentItem.src}
                    className="carouselVideo"
                    playsInline
                    preload="metadata"
                    controls
                    onPlay={(e) => {
                      if (onVideoPlay) {
                        onVideoPlay(e.target); // always pause all other videos
                      }
                    }}
                  />
                </div>
              )}

              {currentItem?.type === 'vimeo' && (
                <div className="vimeoWrapper" style={{ position: 'relative' }}>
                  <iframe
                    src={currentItem.src}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title="Vimeo Video"
                  />
                  <button
                    className="vimeoFullscreenButton"
                    onClick={() => setEnglargedImage(currentIndex)}
                  >
                    ⤢
                  </button>
                </div>
              )}

              {currentItem?.type === 'instagram' && (
                <div className="instagramWrapper">
                  <blockquote
                    className="instagram-media"
                    data-instgrm-permalink={currentItem.src}
                    data-instgrm-version="14"
                  />
                </div>
              )}

              {carouselItems.length > 1 && (
                <div className="carouselControls">
                  <button onClick={prevItem}>&lt;</button>
                  <button onClick={nextItem}>&gt;</button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className='pieceInfo'>
          {artistNames.length > 0 && (
            <div className="artistNames">
              {artistNames.map((artist, index) => {
                const artistRows = artistBios?.[artist.id] || {};

                const artistRow =
                  artistRows[role] ||
                  artistRows.artist ||
                  artistRows.performer ||
                  null;

                return (
                  <img
                    key={index}
                    src={artist.img}
                    alt={artist.name}
                    className={`artistName clickable`}
                    loading="lazy"
                    onClick={() => {
                      if (!artistRow) return;
                      setActiveArtist(artistRow);
                      setIsModalOpen(true);
                    }}
                  />
                );
              })}
            </div>
          )}

          {piece["Piece Name"] && (
            <div className='pieceName'>{piece["Piece Name"]}</div>
          )}
          
          {renderDetails(piece.Details)}

          <div className='pieceDescriptionContainer'>
            <div className="pieceDescription">
              {piece.Description.split('\\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ArtistModal
        isOpen={isModalOpen}
        artist={activeArtist}
        onClose={() => setIsModalOpen(false)}
      />

      <ImageModal
        isOpen={enlargedImage !== null}
        onClose={() => setEnglargedImage(null)}
        images={carouselItems.map(item => {
          if (item.type === 'media') {
            const { image, video } = resolveMediaSource(item.sources);

            // only include the one that exists (matching carousel logic)
            if (image) {
              return {
                type: 'media',
                image: image.replace('.webp', '_hr.webp'),
              };
            }
            return {
              type: 'media',
              video: video.replace('.webm', '_hr.webm'),
            };
          }

          return {
            type: item.type,
            src: item.src,
          };
        })}
        startIndex={enlargedImage || 0}
      />
    </div>
  );
}

export default Plaque;
